import React, { useState, useEffect, useCallback } from 'react'
import supabase from '../supabaseClient'
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
} from '@mui/material'
import {
  School as SchoolIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { useAuth } from '../context/AuthContext'
import { getTeacherAccessibleClasses, getClassDisplayName } from '../utils/classUtils'
import { StyledCard } from './ui/StyledComponents'

const StudentNotes = ({
  studentId,
  showAddNote = true,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { user, userRole } = useAuth()
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [description, setDescription] = useState('')
  const [type, setType] = useState('progress')
  const [openDialog, setOpenDialog] = useState(false)
  const [currentNote, setCurrentNote] = useState(null)
  const [students, setStudents] = useState([])
  const [selectedStudentId, setSelectedStudentId] = useState(studentId || '')
  const [classes, setClasses] = useState([])
  const [selectedClassId, setSelectedClassId] = useState('')

  // Fetch classes for the teacher/admin
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        if (userRole === 'admin' || userRole === 'super_admin') {
          // Admin sees all classes
          const { data, error } = await supabase.from('classes').select('*')
          if (error) throw error
          setClasses(data.map(cls => ({ ...cls, isSubstitute: false })))
        } else if (user?.id) {
          // Teachers see their accessible classes (regular + substitute)
          const data = await getTeacherAccessibleClasses(user.id, '*')
          setClasses(data)
        }
      } catch (error) {
        console.error('Error fetching classes:', error)
        setClasses([])
      }
    }

    fetchClasses()
  }, [user?.id, userRole])

  // Fetch students for dropdown selection if no studentId is provided
  useEffect(() => {
    if (!studentId) {
      if (selectedClassId) {
        fetchStudentsByClass(selectedClassId)
      } else {
        setStudents([])
      }
    } else {
      setSelectedStudentId(studentId)
    }
  }, [studentId, selectedClassId])

  const fetchStudentsByClass = async (classId) => {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('id, first_name, last_name, class_ids')
        .contains('class_ids', [classId])
        .eq('is_active', true)

      if (error) throw error
      setStudents(data || [])
    } catch (error) {
      console.error('Error fetching students:', error)
      setStudents([])
    }
  }

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('student_notes')
        .select('*')
        .eq('student_id', selectedStudentId)
        .order('created_at', { ascending: false })

      // If we're in class-selection mode (no studentId prop), filter by class too
      if (!studentId && selectedClassId) {
        query = query.eq('class_id', selectedClassId)
      }

      const { data, error } = await query

      if (error) throw error
      setNotes(data || [])
    } catch (error) {
      console.error('Error fetching notes:', error)
    } finally {
      setLoading(false)
    }
  }, [selectedStudentId, selectedClassId, studentId])

  // Fetch notes whenever selectedStudentId changes
  useEffect(() => {
    if (selectedStudentId) {
      fetchNotes()
    }
  }, [selectedStudentId, fetchNotes])

  const handleAddNote = async () => {
    try {
      if (!description.trim() || !selectedStudentId || !selectedClassId) return

      const newNote = {
        description,
        type,
        student_id: selectedStudentId,
        class_id: selectedClassId,
        created_at: new Date(),
        updated_at: new Date(),
      }

      const { data, error } = await supabase
        .from('student_notes')
        .insert([newNote])
        .select()

      if (error) throw error

      setNotes([data[0], ...notes])
      setDescription('')
      setType('general')
    } catch (error) {
      console.error('Error adding note:', error)
    }
  }

  const handleEditNote = (note) => {
    setCurrentNote(note)
    setDescription(note.description)
    setType(note.type)
    setOpenDialog(true)
  }

  const handleUpdateNote = async () => {
    try {
      if (!description.trim()) return

      const { error } = await supabase
        .from('student_notes')
        .update({
          description,
          type,
          updated_at: new Date(),
        })
        .eq('id', currentNote.id)

      if (error) throw error

      setNotes(
        notes.map((note) =>
          note.id === currentNote.id
            ? { ...note, description, type, updated_at: new Date() }
            : note,
        ),
      )

      handleCloseDialog()
    } catch (error) {
      console.error('Error updating note:', error)
    }
  }

  const handleDeleteNote = async (id) => {
    try {
      // Add confirmation dialog
      if (!window.confirm("Are you sure you want to delete this note?")) {
        return; // Exit if user cancels
      }
      
      const { error } = await supabase
        .from('student_notes')
        .delete()
        .eq('id', id)

      if (error) throw error

      setNotes(notes.filter((note) => note.id !== id))
    } catch (error) {
      console.error('Error deleting note:', error)
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setCurrentNote(null)
    setDescription('')
    setType('general')
  }

  const handleClassChange = (classId) => {
    setSelectedClassId(classId)
    setSelectedStudentId('') // Reset student selection when class changes
    setNotes([]) // Clear notes when class changes
  }

  const noteTypes = [
    { value: 'general', label: 'General' },
    { value: 'academic', label: 'Academic' },
    { value: 'behavioral', label: 'Behavioral' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'parent_communication', label: 'Parent Communication' },
  ]

  // Get student full name for display
  const getStudentFullName = (student) => {
    return `${student.first_name} ${student.last_name}`
  }

  // Add a function to get color based on note type
  const getNoteTypeColor = (type) => {
    switch (type) {
      case 'academic':
        return theme.palette.primary.main // primary blue
      case 'behavioral':
        return theme.palette.warning.main // warning orange
      case 'attendance':
        return theme.palette.error.main // error red
      case 'parent_communication':
        return theme.palette.success.main // success green
      default:
        return theme.palette.text.secondary // grey
    }
  }

  return (
    <>
      {!studentId && (
        <StyledCard sx={{ mb: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
            <SchoolIcon sx={{ 
              mr: { xs: 1, sm: 1.5 }, 
              color: 'primary.main',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }} />
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}>
              Select Class & Student
            </Typography>
          </Box>
          
          <Stack spacing={{ xs: 2, sm: 3 }}>
            {/* Class Selector */}
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Select Class
              </InputLabel>
              <Select
                value={selectedClassId}
                onChange={(e) => handleClassChange(e.target.value)}
                label="Select Class"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiMenuItem-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Choose a class to begin</em>
                </MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <SchoolIcon sx={{ 
                        mr: 1, 
                        fontSize: { xs: 18, sm: 20 }, 
                        color: 'text.secondary' 
                      }} />
                      {getClassDisplayName(cls)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Student Selector - only show if class is selected */}
            {selectedClassId && (
              <Fade in={true}>
                <Autocomplete
                  id="student-select"
                  options={students}
                  getOptionLabel={(option) => getStudentFullName(option)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and select student"
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <PersonIcon sx={{ 
                            mr: 1, 
                            color: 'text.secondary',
                            fontSize: { xs: '1.125rem', sm: '1.25rem' },
                          }} />
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <PersonIcon sx={{ 
                          mr: { xs: 1, sm: 1.5 }, 
                          fontSize: { xs: 18, sm: 20 }, 
                          color: 'text.secondary' 
                        }} />
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {getStudentFullName(option)}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  onChange={(event, newValue) => {
                    setSelectedStudentId(newValue ? newValue.id : '')
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={students.find(student => student.id === selectedStudentId) || null}
                />
              </Fade>
            )}
          </Stack>
        </StyledCard>
      )}

      {/* Show message when no class is selected */}
      {!studentId && !selectedClassId && (
        <Typography 
          variant="subtitle2" 
          color="text.secondary" 
          sx={{ 
            mt: { xs: 1.5, sm: 2 }, 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          Please select a class first to view students and manage notes.
        </Typography>
      )}

      {/* Show message when class is selected but no student */}
      {!studentId && selectedClassId && !selectedStudentId && (
        <Typography 
          variant="subtitle2" 
          color="text.secondary" 
          sx={{ 
            mt: { xs: 1.5, sm: 2 }, 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          Please select a student from the class to view or add notes.
        </Typography>
      )}

      {(selectedStudentId && (studentId || selectedClassId)) && (
        <>
          {showAddNote && (
            <Paper elevation={3} sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              mb: { xs: 2, sm: 3 },
            }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                Add New Note
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Note Type
                </InputLabel>
                <Select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  label="Note Type"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '& .MuiMenuItem-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                >
                  {noteTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Note Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddNote}
                fullWidth={isMobile}
                sx={{ 
                  mt: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '10px 16px', sm: '12px 24px' },
                }}
              >
                Add Note
              </Button>
            </Paper>
          )}

          {loading ? (
            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Loading notes...
            </Typography>
          ) : (
            <>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                Notes History
              </Typography>
              {notes.length === 0 ? (
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  No notes found for this student.
                </Typography>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  {/* Timeline center line - hide on mobile */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: '20px', sm: '50%' },
                      top: 0,
                      bottom: 0,
                      width: 2,
                      bgcolor: 'divider',
                      transform: { xs: 'none', sm: 'translateX(-50%)' },
                      zIndex: 0,
                      display: { xs: 'none', sm: 'block' },
                    }}
                  />
                  
                  {/* Year markers along timeline - hide on mobile */}
                  {(() => {
                    // Get unique years from notes
                    const years = [...new Set(notes.map(note => 
                      new Date(note.created_at).getFullYear()
                    ))].sort((a, b) => b - a); // Sort descending
                    
                    return years.map((year, index) => (
                      <Box
                        key={`year-${year}`}
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          // Position evenly along timeline
                          top: `${(index * 100) / Math.max(1, years.length - 1)}%`,
                          transform: 'translateX(-50%)',
                          zIndex: 2,
                          display: { xs: 'none', sm: 'flex' },
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 32, sm: 36, md: 40 },
                            height: { xs: 32, sm: 36, md: 40 },
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            boxShadow: 3,
                            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                          }}
                        >
                          {year}
                        </Box>
                      </Box>
                    ));
                  })()}
                  
                  {notes.map((note, index) => (
                    <Box
                      key={note.id}
                      sx={{
                        display: 'flex',
                        mb: { xs: 3, sm: 4 },
                        position: 'relative',
                        justifyContent: { xs: 'flex-start', sm: index % 2 === 0 ? 'flex-start' : 'flex-end' },
                      }}
                    >
                      {/* Timeline dot - position on left for mobile */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: { xs: '20px', sm: '50%' },
                          top: 20,
                          width: { xs: 12, sm: 14, md: 16 },
                          height: { xs: 12, sm: 14, md: 16 },
                          borderRadius: '50%',
                          bgcolor: getNoteTypeColor(note.type),
                          transform: { xs: 'translateX(-50%)', sm: 'translateX(-50%)' },
                          zIndex: 1,
                          boxShadow: '0 0 0 4px rgba(255,255,255,0.8)',
                        }}
                      />
                      
                      {/* Content */}
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          p: { xs: 1.5, sm: 2 }, 
                          width: { xs: 'calc(100% - 40px)', sm: '45%' },
                          ml: { xs: '40px', sm: index % 2 === 0 ? 0 : 'auto' },
                          mr: { xs: 0, sm: index % 2 === 0 ? 'auto' : 0 },
                          position: 'relative',
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          borderLeft: `4px solid ${getNoteTypeColor(note.type)}`,
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: theme.shadows[4],
                          },
                          '&::before': {
                            content: { xs: 'none', sm: '""' },
                            position: 'absolute',
                            top: 20,
                            width: 0,
                            height: 0,
                            border: '8px solid transparent',
                            ...(index % 2 === 0 
                              ? {
                                  right: '-16px',
                                  borderLeftColor: theme.palette.background.paper,
                                } 
                              : {
                                  left: '-16px',
                                  borderRightColor: theme.palette.background.paper,
                                }
                            ),
                          },
                          background: `linear-gradient(to bottom, ${
                            note.type === 'academic' ? theme.palette.primary.light + '10' : 
                            note.type === 'behavioral' ? theme.palette.warning.light + '10' :
                            note.type === 'attendance' ? theme.palette.error.light + '10' :
                            note.type === 'parent_communication' ? theme.palette.success.light + '10' :
                            theme.palette.grey[200] + '10'
                          }, transparent)`,
                        }}
                      >
                        {/* Mobile: Action icons at top right */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: { xs: 8, sm: 16 },
                            right: { xs: 8, sm: 16 },
                            display: { xs: 'flex', sm: 'none' },
                            gap: 0.5,
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditNote(note)}
                            sx={{ 
                              padding: 0.5,
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteNote(note.id)}
                            sx={{ 
                              padding: 0.5,
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          display="block"
                          sx={{ 
                            fontWeight: 'medium',
                            mb: 1,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            textAlign: { xs: 'center', sm: 'left' },
                            pr: { xs: 5, sm: 0 }, // Add padding on mobile to avoid overlap with icons
                          }}
                        >
                          {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                        </Typography>
                        {/* Note Type - Small and subtle */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            textTransform: 'capitalize',
                            fontWeight: 'normal',
                            color: getNoteTypeColor(note.type),
                            fontSize: { xs: '0.6rem', sm: '0.75rem' },
                            opacity: { xs: 0.6, sm: 0.8 },
                            mb: { xs: 0.5, sm: 0.75 },
                            textAlign: { xs: 'center', sm: 'left' },
                          }}
                        >
                          {note.type.replace('_', ' ')}
                        </Typography>
                        
                        {/* Desktop: Action buttons */}
                        <Box sx={{ 
                          display: { xs: 'none', sm: 'flex' },
                          flexDirection: 'row',
                          gap: 1,
                          width: 'auto',
                          flexShrink: 0,
                          position: 'absolute',
                          top: 16,
                          right: 16,
                        }}>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => handleEditNote(note)}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1,
                              fontSize: '0.875rem',
                              textTransform: 'none',
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleDeleteNote(note.id)}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1,
                              fontSize: '0.875rem',
                              textTransform: 'none',
                            }}
                          >
                            Delete
                          </Button>
                        </Box>

                        {/* Note Message - Main content */}
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            mt: { xs: 0.5, sm: 0 },
                            mb: { xs: 0.5, sm: 0 },
                            lineHeight: 1.6,
                            whiteSpace: 'pre-line',
                            fontSize: { xs: '0.9375rem', sm: '1rem' },
                            textAlign: { xs: 'center', sm: 'left' },
                            fontWeight: { xs: 400, sm: 400 },
                          }}
                        >
                          {note.description}
                        </Typography>
                        {note.updated_at !== note.created_at && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              display: 'block',
                              mt: 1,
                              fontStyle: 'italic',
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            Updated: {format(new Date(note.updated_at), 'MMM d, yyyy h:mm a')}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Edit Note Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: { xs: 1, sm: 2 } }}>
          Edit Note
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Note Type
            </InputLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              label="Note Type"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '& .MuiMenuItem-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              {noteTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Note Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={handleCloseDialog}
            fullWidth={isMobile}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateNote} 
            color="primary"
            fullWidth={isMobile}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default StudentNotes
